import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cx } from './util.js';

export interface FieldFrameProps {
  label: string;
  supporting?: ReactNode;
  error?: string | undefined;
  /** The big money field: a large condensed amount, with the rupee sign as prefix. */
  money?: boolean;
  className?: string | undefined;
}

export interface TextFieldProps
  extends FieldFrameProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'className'> {
  prefix?: ReactNode;
}

/** An outlined text field. The label sits above the box so it never hides the value. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, supporting, error, prefix, money = false, className, id, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const noteId = `${inputId}-note`;
  const hasNote = error !== undefined || supporting !== undefined;
  return (
    <div
      className={cx(
        'k-field',
        error !== undefined && 'k-field--error',
        money && 'k-field--money',
        className,
      )}
    >
      <label htmlFor={inputId} className="k-field__label k-t-label-sm">
        {label}
      </label>
      <div className="k-field__box">
        {prefix === undefined ? null : <span className="k-field__prefix">{prefix}</span>}
        <input
          {...rest}
          ref={ref}
          id={inputId}
          className={cx('k-field__input', money ? 'k-t-money' : 'k-t-body-lg')}
          aria-invalid={error !== undefined || undefined}
          aria-describedby={hasNote ? noteId : undefined}
        />
      </div>
      {hasNote ? (
        <p
          id={noteId}
          className="k-field__note k-t-label-sm"
          role={error === undefined ? undefined : 'alert'}
        >
          {error ?? supporting}
        </p>
      ) : null}
    </div>
  );
});

export interface TextAreaProps
  extends FieldFrameProps,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, supporting, error, className, id, rows = 3, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? auto;
  const noteId = `${inputId}-note`;
  const hasNote = error !== undefined || supporting !== undefined;
  return (
    <div className={cx('k-field', error !== undefined && 'k-field--error', className)}>
      <label htmlFor={inputId} className="k-field__label k-t-label-sm">
        {label}
      </label>
      <div className="k-field__box">
        <textarea
          {...rest}
          ref={ref}
          id={inputId}
          rows={rows}
          className="k-field__input k-t-body-lg"
          aria-invalid={error !== undefined || undefined}
          aria-describedby={hasNote ? noteId : undefined}
        />
      </div>
      {hasNote ? (
        <p
          id={noteId}
          className="k-field__note k-t-label-sm"
          role={error === undefined ? undefined : 'alert'}
        >
          {error ?? supporting}
        </p>
      ) : null}
    </div>
  );
});
